import { Component, OnInit, inject } from '@angular/core';
import {ProjectResponse} from "../../../models/projects/projectResponse";
import {NgxSpinnerService} from "ngx-spinner";
import {ActivatedRoute, Router} from "@angular/router";
import {GrantApplicationService} from "../../../services/grantApplication.service";
import {
  baseFile,
  GrantApplicationGetInforResponse
} from "../../../models/grantApplication/grantApplicationGetinfoResponse";
import {FormArray, FormGroup} from "@angular/forms";
import Swal from "sweetalert2";
import swal from "sweetalert2";
import {UploadFileService} from "../../../services/upload-file.service";
import { SweetAlertService } from '../../../services/sweet-alert.service';


export type GrantApplicationStatus =
  "Finalized"
  | "Not Approved"
  | "Not Submitted"
  | "On Hold"
  | "Submitted"
  | "Approved";

@Component({
  selector: 'app-application-detail',
  templateUrl: './application-detail.component.html',
  styleUrls: ['./application-detail.component.scss']
})
export class ApplicationDetailComponent implements OnInit {
  /** Routed through the service so it is suppressed while the session is being torn down;
   * a raw Swal.fire() here would replace the sign-out notice and hide why it appeared. */
  private readonly sweetAlertService = inject(SweetAlertService);

  id: string = "";
  url: any;
  projectData: ProjectResponse = new ProjectResponse();

  getinfoResposne: GrantApplicationGetInforResponse | undefined
  fileForm: FormGroup = new FormGroup({
    documentDatas: new FormArray([]),
  });
  files: baseFile[] = [];
  protected projectId: string = "";
  protected applicationId: string = "";
  protected status: GrantApplicationStatus | undefined = undefined;
  protected isReadOnly: boolean = false;
  protected isFormLoaded: boolean = false;

  private readonly ALERT_MESSAGES = {
    invalidFiles: 'Please ensure all files are valid before submitting them.',
    uploadFailed: 'Failed to upload documents. Please try again!',
    fetchFailed: 'Failed to fetch the updated documents.',
  };
  protected grantApplicationType: string | null = null;

  constructor(
    private grantApplicationService: GrantApplicationService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private route: ActivatedRoute,
    private uploadFile: UploadFileService,
  ) {

  }

  get documentDatas(): FormArray {
    return this.fileForm.get('documentDatas') as FormArray;
  }

  ngOnInit(): void {
    this.route.params.subscribe(async params => {
      this.applicationId = params['appid'];

      const statusParam = this.route.snapshot.queryParamMap.get('status');
      const grantApplicationTypeParam = this.route.snapshot.queryParamMap.get('grantApplicationType') as string | null;
      this.grantApplicationType = grantApplicationTypeParam
      this.status = (statusParam as GrantApplicationStatus) ?? undefined;
      

      if (!this.applicationId || !this.status || !grantApplicationTypeParam) {

        await this.router.navigate(['/applications']);
      } else {

        await this.loadApplication();

        this.handleUpdatedFilesFetch();

      }
    });
  }

  async loadApplication() {

    await this.spinner.show();
    this.grantApplicationService.getInfo(this.applicationId).subscribe(async res => {
      if (res && res.result) {

        this.getinfoResposne = res.item ?? undefined;

        this.projectId = res.item?.projectInfo?.projectId ?? "";

        this.projectData = res.item?.projectInfo ?? new ProjectResponse();

        this.files = res.item?.files ?? []
        // var appStatus = (res.item?.status as GrantApplicationStatus) ?? undefined;// (statusParam as GrantApplicationStatus) ?? undefined;
        const appStatus: GrantApplicationStatus = (res.item?.status as GrantApplicationStatus) ?? undefined;
        
        this.isReadOnly = ["Not Approved","On Hold","Finalized"].includes(appStatus);
      } else {
      const errorMessage = res?.messages;
      await this.spinner.hide();
      swal.fire({
        title: "Error",
        text: errorMessage?.join(', ') || "An unexpected error occurred.",
        icon: "error",
      }).then(() => {

        this.router.navigate(['/applications']);
      });
      return;
    }
      await this.spinner.hide();
      this.isFormLoaded = true;
    });
  }

  async onSubmit() {

    if (this.fileForm.invalid) {
      const errorDetails = this.getFileValidationErrors();
      await this.showAlert('warning', 'Invalid Files', this.ALERT_MESSAGES.invalidFiles + '\n\n' + errorDetails);
      return;
    }

    await this.spinner.show();

    const formData = this.createUploadFormData();
    this.handleDocumentUpload(formData);
  }

  private getFileValidationErrors(): string {
    const errors: string[] = [];

    this.documentDatas.controls.forEach((control: any, index: number) => {
      const fileControl = control.get('file');
      if (fileControl && fileControl.errors) {
        const fileName = control.get('name')?.value || `File ${index + 1}`;

        if (fileControl.errors.required) {
          errors.push(`${fileName}: No file selected.`);
        }

        if (fileControl.errors.maxSize) {
          const actual = fileControl.errors.maxSize.actual.toFixed(2);
          const max = fileControl.errors.maxSize.max;
          errors.push(`${fileName}: File size (${actual} MB) exceeds the maximum allowed size (${max} MB).`);
        }

        if (fileControl.errors.fileType) {
          const actual = fileControl.errors.fileType.actual;
          errors.push(`${fileName}: File type (${actual}) is not allowed.`);
        }
      }
    });

    return errors.length > 0 ? errors.join('\n') : 'Please check that all files meet the required criteria.';
  }

  clearUploadedFiles(): void {

    while (this.documentDatas.length !== 0) {
      this.documentDatas.removeAt(0);
    }
    console.log('Uploaded files cleared from the form.');
  }

  private createUploadFormData(): FormData {
    const formData = new FormData();
    formData.append('ApplicationId', this.applicationId);
    this.documentDatas.controls.forEach((control: any) => {
      if (control.value.file) {
        this.appendFileToFormData(formData, control.value.file);
      }
    });
    return formData;
  }

  private appendFileToFormData(formData: FormData, file: File): void {
    formData.append('Files', file, file.name);
  }

  private handleDocumentUpload(formData: FormData): void {
    this.grantApplicationService.upLoadDocument(formData).subscribe({
      next: async (value) => {
        if (value.result) {
          console.log('Document uploaded successfully');
          this.clearUploadedFiles();
        } else {
          await this.handleError("", 'error')
        }

      },
      error: (error) => this.handleError(error, 'error'),
      complete: () => {
        this.spinner.hide();
        this.handleUpdatedFilesFetch();
      },
    });
  }

  private handleUpdatedFilesFetch(): void {
    this.grantApplicationService.getDocs(this.applicationId).subscribe({
      next: (response) => {
        if (response.result) {
          this.files = response.item ?? [];
          this.uploadFile.setFile(this.files);
          console.log('Documents fetched successfully:', this.files);
        }
      },
      error: (error) => this.handleError(error, 'error'),
    });
  }

  private async showAlert(icon: 'warning' | 'error', title: string, text: string): Promise<void> {
    await this.sweetAlertService.fire({
      icon,
      title,
      html: text.replace(/\n/g, '<br>'),
      confirmButtonText: 'OK',
    });
  }


  private async handleError(error: any, title: string) {
    console.error(title, error);
  }


}
